import { Skeleton } from "@/lib/components/ui/skeleton";
import { Token } from "@/lib/classes/BaseNetwork";
import { useWidget } from "@/lib/hooks/use-widget";
import { cn, formatCurrency } from "@/lib/utils";
import { useStytchUser } from "@stytch/react";
import React, { FC } from "react";

interface Props {
  token: Token & {
    value: number;
    image: string;
    platformImages: Record<string, string>;
  };
}

const BalanceRow: FC<Props> = ({ token }) => {
  const { isLoading: isBalancesLoading } = useWidget();
  const { user } = useStytchUser();

  const currency = (user?.untrusted_metadata.currency as string) || "USD";

  return (
    <div className="flex items-center gap-3 justify-between text-base mb-1">
      <div className="flex items-center gap-2">
        {token.image ? (
          <img
            src={token.image}
            className={cn(
              "h-8 w-8",
              token.symbol === "ETH" && "rounded-full bg-gray-200",
            )}
          />
        ) : (
          <div className="h-8 w-8" />
        )}
        <div className="flex flex-col">
          <div className="text-base font-bold whitespace-nowrap overflow-hidden max-w-48 text-ellipsis">
            {token.name}
          </div>

          <div className="flex gap-1">
            <div className="text-xs text-muted-foreground">{token.symbol}</div>
            <div className="flex items-center">
              {Object.entries(token.platformImages).map(
                ([chain, image], index) => (
                  <div
                    key={index}
                    className={cn(
                      "rounded-full overflow-hidden border border-white",
                      index === 0 ? "ml-0 z-0" : "-ml-2 z-10",
                      chain === "ETH" && "bg-gray-200",
                    )}
                  >
                    <img
                      src={image}
                      alt={chain}
                      width={14}
                      height={14}
                      className="w-3.5 h-3.5 object-contain"
                    />
                  </div>
                ),
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end space-x-1 overflow-hidden">
        {isBalancesLoading ? (
          <Skeleton className="h-4 w-16" />
        ) : (
          <div className="flex flex-col items-end text-right">
            <div className="text-base font-bold font-chivo">
              {formatCurrency(token.value, currency)}
            </div>
            <div className="flex items-center text-muted-foreground">
              <span className="text-xs truncate max-w-[80px]">
                {token.balanceFormatted}
              </span>
              <span className="text-xs ml-1">{token.symbol}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BalanceRow;
